/**
 * Shared hex-grid helper (client side).
 *
 * Pointy-top hexagons in axial coordinates. Math is identical to the
 * server mirror at `frontend/functions/_lib/hex.ts` — keep both in
 * sync when changing direction layout or coordinate conventions.
 */

export const HEX_DIRS = 6;

export const HEX_DIR_NAMES = ["NE", "E", "SE", "SW", "W", "NW"] as const;

const AXIAL_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, -1],
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [0, -1],
];

export function oppositeDir(dir: number): number {
  return (dir + 3) % HEX_DIRS;
}

export function hexNeighbor(q: number, r: number, dir: number): { q: number; r: number } {
  const [dq, dr] = AXIAL_OFFSETS[dir];
  return { q: q + dq, r: r + dr };
}

export function neighborsBounded(idx: number, gridW: number, gridH: number): number[] {
  const q = idx % gridW;
  const r = Math.floor(idx / gridW);
  const out = new Array<number>(HEX_DIRS);
  for (let dir = 0; dir < HEX_DIRS; dir++) {
    const n = hexNeighbor(q, r, dir);
    out[dir] = n.q < 0 || n.q >= gridW || n.r < 0 || n.r >= gridH
      ? -1
      : n.r * gridW + n.q;
  }
  return out;
}

export function neighborsTorus(idx: number, gridW: number, gridH: number): number[] {
  const q = idx % gridW;
  const r = Math.floor(idx / gridW);
  const out = new Array<number>(HEX_DIRS);
  for (let dir = 0; dir < HEX_DIRS; dir++) {
    const n = hexNeighbor(q, r, dir);
    const wq = ((n.q % gridW) + gridW) % gridW;
    const wr = ((n.r % gridH) + gridH) % gridH;
    out[dir] = wr * gridW + wq;
  }
  return out;
}

export function hexWorldXZ(
  q: number,
  r: number,
  gridW: number,
  gridH: number,
  cellSize: number,
): { x: number; z: number } {
  const centerX = (gridW - 1) / 2 + (gridH - 1) / 4;
  const centerZ = (gridH - 1) / 2;
  const x = cellSize * (q + r / 2 - centerX);
  const z = cellSize * (Math.sqrt(3) / 2) * (r - centerZ);
  return { x, z };
}
